import React from 'react';
import { Box, Typography, Alert, CircularProgress } from '@mui/material';
import {
  People as PeopleIcon,
  AccountBalance as LoanIcon,
  Description as ApplicationIcon,
  TrendingUp as TrendingIcon,
  MonetizationOn as MoneyIcon,
  Warning as WarningIcon,
  Payment as PaymentIcon,
  AttachMoney as DisbursementIcon,
  PhoneAndroid as FeeIcon,
  CheckCircle as ApprovedIcon,
  Cancel as RejectedIcon,
  ShowChart as ParIcon,
  Percent as CollectionRateIcon,
  Savings as RevenueIcon,
  Receipt as FeeIncomeIcon,
  AccountBalanceWallet as InterestIcon,
} from '@mui/icons-material';
import { useDashboardData } from '../../hooks/dashboard/useDashboardData';
import StatCard from '../../components/dashboard/StatCard';
import RecentActivities from '../../components/dashboard/RecentActivities';
import LoanDistributionChart from '../../components/dashboard/LoanDistributionChart';
import CollectionsChart from '../../components/dashboard/CollectionsChart';
import QuickActions from '../../components/dashboard/QuickActions';
import AlertsPanel from '../../components/dashboard/AlertsPanel';
import { useAuth } from '../../hooks/auth/useAuth';

const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-KE', {
    style: 'currency',
    currency: 'KES',
    minimumFractionDigits: 0,
  }).format(amount);
};

const formatPercent = (value: number): string => {
  return `${value.toFixed(2)}%`;
};

const Dashboard: React.FC = () => {
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = useDashboardData();

  // Handle error state
  if (error) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Dashboard
        </Typography>
        <Alert severity="error" sx={{ mt: 2 }}>
          Failed to load dashboard data. {error instanceof Error ? error.message : 'Please try again.'}
        </Alert>
      </Box>
    );
  }

  // Handle loading state for first load
  if (isLoading && !data) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 3 }} color="text.secondary">
          Loading dashboard...
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Dashboard
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Welcome back, {user?.fullName || user?.username}! Here's your overview.
        </Typography>
      </Box>

      {/* Row 1 — Customers & Loans */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 3,
        }}
      >
        <StatCard
          title="Total Customers"
          value={data?.totalCustomers?.toLocaleString() ?? '0'}
          icon={<PeopleIcon />}
          color="#1976d2"
          isLoading={isLoading}
          subtitle={`${data?.activeCustomers ?? 0} active`}
        />
        <StatCard
          title="Active Loans"
          value={data?.totalActiveLoans?.toLocaleString() ?? '0'}
          icon={<LoanIcon />}
          color="#2e7d32"
          isLoading={isLoading}
          subtitle="Currently disbursed"
        />
        <StatCard
          title="Overdue Loans"
          value={data?.overdueLoans?.toLocaleString() ?? '0'}
          icon={<WarningIcon />}
          color="#d32f2f"
          isLoading={isLoading}
          subtitle={formatCurrency(data?.totalOverdueAmount ?? 0) + ' overdue'}
        />
        <StatCard
          title="Total Portfolio"
          value={formatCurrency(data?.totalOutstanding ?? 0)}
          icon={<MoneyIcon />}
          color="#0288d1"
          isLoading={isLoading}
          subtitle="Outstanding balance"
        />
      </Box>

      {/* Row 2 — Applications */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 3,
        }}
      >
        <StatCard
          title="Total Applications"
          value={data?.totalApplications?.toLocaleString() ?? '0'}
          icon={<ApplicationIcon />}
          color="#455a64"
          isLoading={isLoading}
          subtitle="All time"
        />
        <StatCard
          title="Pending Review"
          value={data?.pendingApplications?.toLocaleString() ?? '0'}
          icon={<ApplicationIcon />}
          color="#ed6c02"
          isLoading={isLoading}
          subtitle="Awaiting officer review"
        />
        <StatCard
          title="Awaiting Fee Payment"
          value={data?.pendingFeePaymentApplications?.toLocaleString() ?? '0'}
          icon={<FeeIcon />}
          color="#f57c00"
          isLoading={isLoading}
          subtitle="M-Pesa fee pending"
        />
        <StatCard
          title="Approved / Rejected"
          value={`${data?.approvedApplications ?? 0} / ${data?.rejectedApplications ?? 0}`}
          icon={<ApprovedIcon />}
          color="#388e3c"
          isLoading={isLoading}
          subtitle="Approved · Rejected"
        />
      </Box>

      {/* Row 3 — Collections */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 3,
        }}
      >
        <StatCard
          title="Total Disbursed"
          value={formatCurrency(data?.totalDisbursed ?? 0)}
          icon={<DisbursementIcon />}
          color="#00796b"
          isLoading={isLoading}
          subtitle="All time"
        />
        <StatCard
          title="Collections Today"
          value={formatCurrency(data?.collectedToday ?? 0)}
          icon={<TrendingIcon />}
          color="#9c27b0"
          isLoading={isLoading}
        />
        <StatCard
          title="Collections (WTD)"
          value={formatCurrency(data?.collectedThisWeek ?? 0)}
          icon={<PaymentIcon />}
          color="#6a1b9a"
          isLoading={isLoading}
          subtitle="This week"
        />
        <StatCard
          title="Collections (MTD)"
          value={formatCurrency(data?.collectedThisMonth ?? 0)}
          icon={<PaymentIcon />}
          color="#7b1fa2"
          isLoading={isLoading}
          subtitle="This month"
        />
      </Box>

      {/* Row 4 — Revenue */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
          gap: 3,
          mb: 3,
        }}
      >
        <StatCard
          title="Total Revenue"
          value={formatCurrency(data?.totalRevenue ?? 0)}
          icon={<RevenueIcon />}
          color="#1565c0"
          isLoading={isLoading}
          subtitle="All income streams"
        />
        <StatCard
          title="Processing Fees"
          value={formatCurrency(data?.processingFeeIncome ?? 0)}
          icon={<FeeIncomeIcon />}
          color="#0277bd"
          isLoading={isLoading}
          subtitle="Fee income collected"
        />
        <StatCard
          title="Interest Income"
          value={formatCurrency(data?.interestIncome ?? 0)}
          icon={<InterestIcon />}
          color="#00838f"
          isLoading={isLoading}
          subtitle="Interest earned"
        />
        <StatCard
          title="Penalty Income"
          value={formatCurrency(data?.penaltyIncome ?? 0)}
          icon={<WarningIcon />}
          color="#c62828"
          isLoading={isLoading}
          subtitle="Late payment penalties"
        />
      </Box>

      {/* Row 5 — Portfolio Quality */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
          gap: 3,
          mb: 4,
        }}
      >
        <StatCard
          title="PAR > 30 Days"
          value={formatPercent(data?.par30 ?? 0)}
          icon={<ParIcon />}
          color={(data?.par30 ?? 0) > 5 ? '#c62828' : '#2e7d32'}
          isLoading={isLoading}
          subtitle="Portfolio at risk"
        />
        <StatCard
          title="NPL Ratio"
          value={formatPercent(data?.npl ?? 0)}
          icon={<RejectedIcon />}
          color={(data?.npl ?? 0) > 5 ? '#c62828' : '#2e7d32'}
          isLoading={isLoading}
          subtitle="Non-performing loans"
        />
        <StatCard
          title="Collection Rate"
          value={formatPercent(data?.collectionRate ?? 0)}
          icon={<CollectionRateIcon />}
          color={(data?.collectionRate ?? 0) < 80 ? '#c62828' : '#2e7d32'}
          isLoading={isLoading}
          subtitle="Repayment rate"
        />
      </Box>

      {/* Quick Actions */}
      <Box sx={{ mb: 4 }}>
        <QuickActions onRefresh={() => refetch()} />
      </Box>

      {/* Charts Section */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(2, 1fr)',
          },
          gap: 3,
          mb: 4,
        }}
      >
        <CollectionsChart
          data={data?.collectionTrends}
          isLoading={isLoading}
        />
        <LoanDistributionChart
          data={data?.loanDistribution}
          isLoading={isLoading}
        />
      </Box>

      {/* Recent Activities and Alerts */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            lg: 'repeat(2, 1fr)',
          },
          gap: 3,
        }}
      >
        <RecentActivities
          loans={data?.recentLoans}
          applications={data?.recentApplications}
          payments={data?.recentPayments}
          isLoading={isLoading}
        />
        <AlertsPanel
          overdueAlerts={data?.overdueAlerts}
          pendingApprovals={data?.pendingApprovals}
          isLoading={isLoading}
        />
      </Box>
    </Box>
  );
};

export default Dashboard;
